import { AfterViewInit, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';
import feather from 'feather-icons';
import { environment } from 'environments/environment';
import { HeaderService } from 'app/services/header/header.service';
import { LogicielCaisseAuthService } from 'app/services/logiciel-caisse/logiciel-caisse-auth.service';
import {
  CaisseClient,
  LogicielCaisseClientService,
} from 'app/services/logiciel-caisse/logiciel-caisse-client.service';
import { LogicielCaisseTicketService } from 'app/services/logiciel-caisse/logiciel-caisse-ticket.service';
import { resolveEnterpriseDisplayName } from 'app/services/enterprise-context';

interface ClientOption {
  id: number;
  label: string;
  email?: string | null;
  phone?: string | null;
}

interface FactureLineRow {
  ligneId?: number;
  reference: string;
  libelle: string;
  couleurTaille: string;
  quantite: number;
  prixUnitaire: number;
  totalHt: number;
  productId?: number | null;
  codeBarre?: string | null;
  couleur?: string | null;
  taille?: string | null;
}

interface SourceTicketRow {
  id: number;
  numero: string;
  date: string;
  entrepot: string;
  statut: string;
  client: string;
  total: number;
}

@Component({
  selector: 'app-logiciel-caisse-facture-detail',
  templateUrl: './logiciel-caisse-facture-detail.component.html',
  styleUrls: ['./logiciel-caisse-facture-detail.component.scss'],
})
export class LogicielCaisseFactureDetailComponent implements OnInit, AfterViewInit {
  factureId = 0;
  returnUrl = '/tiers/ticketCaisse/tous';
  loading = true;
  finalizing = false;
  isEditable = false;
  documentStatut = '—';
  stationLogo = environment.file_url;
  stationName = resolveEnterpriseDisplayName();
  primaryColor = '#134AD3';
  numDocument = '—';
  documentDate = '—';
  entrepotLabel = '—';
  documentOriginalLabel = '—';
  clientLabel = '—';
  clientPhone = '—';
  clientId: number | null = null;
  selectedClientId: number | null = null;
  clientOptions: ClientOption[] = [];
  loadingClients = false;
  savingClient = false;
  ligneRows: FactureLineRow[] = [];
  sourceTickets: SourceTicketRow[] = [];
  totals = { totalHT: 0, totalTVA: 0, totalTTC: 0, timbreFiscal: 1, netAPayer: 0 };

  documentColumnSettings = {
    reference: false,
    quantite: true,
    prixUnitaire: true,
    tva: false,
    afficherCodeBarres: true,
    afficherCoordonneesClients: true,
  };

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly headerService: HeaderService,
    private readonly authService: LogicielCaisseAuthService,
    private readonly ticketService: LogicielCaisseTicketService,
    private readonly clientService: LogicielCaisseClientService,
    private readonly toastr: ToastrService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    const session = this.authService.getSession();
    this.stationName = resolveEnterpriseDisplayName(session?.name);

    this.route.queryParamMap.subscribe((q) => {
      const ret = q.get('return');
      if (ret && ret.startsWith('/')) {
        this.returnUrl = ret;
      }
      const id = Number(q.get('id'));
      if (!Number.isFinite(id) || id <= 0) {
        this.toastr.error('Facture invalide.');
        void this.router.navigateByUrl(this.returnUrl);
        return;
      }
      this.factureId = id;
      this.loadDetail(q.get('edit') === '1');
    });
  }

  ngAfterViewInit(): void {
    feather.replace();
  }

  onPrimaryColorChange(color: string): void {
    if (color?.trim()) {
      this.primaryColor = color.trim();
      this.cdr.detectChanges();
    }
  }

  onDocumentSettingsChange(settings: Record<string, unknown>): void {
    this.documentColumnSettings = { ...this.documentColumnSettings, ...settings };
    this.cdr.detectChanges();
  }

  getColorWithOpacity(opacity: number): string {
    const hex = this.primaryColor.replace('#', '');
    if (hex.length !== 6) return `rgba(19, 74, 211, ${opacity})`;
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  formatPrice(value: number): string {
    return value.toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
  }

  formatStatutLabel(statut: string): string {
    const s = (statut || '').trim();
    if (s === 'termine') return 'Terminé';
    if (s === 'en_cours') return 'En cours';
    if (s === 'annulé' || s === 'annule') return 'Annulé';
    return s || '—';
  }

  get hasClient(): boolean {
    return this.clientId != null && this.clientId > 0;
  }

  get showClientPicker(): boolean {
    return this.isEditable && !this.hasClient;
  }

  goBack(): void {
    void this.router.navigateByUrl(this.returnUrl);
  }

  canFinalize(): boolean {
    return (
      this.isEditable &&
      !this.loading &&
      !this.finalizing &&
      !this.savingClient &&
      this.ligneRows.length > 0 &&
      this.hasClient
    );
  }

  onClientSelected(clientId: number | null): void {
    if (!clientId || !this.isEditable || this.savingClient) {
      return;
    }
    const option = this.clientOptions.find((c) => c.id === clientId);
    if (!option) {
      return;
    }
    this.savingClient = true;
    this.setHeaderConfig();
    this.ticketService
      .updateDocument(this.factureId, {
        client_id: clientId,
        client_email: option.email || null,
        client_phone: option.phone || null,
      })
      .subscribe({
        next: () => {
          this.clientId = clientId;
          this.clientLabel = option.label;
          this.clientPhone = option.phone?.trim() || '—';
          this.savingClient = false;
          this.setHeaderConfig();
          this.cdr.detectChanges();
        },
        error: (err: Error) => {
          this.savingClient = false;
          this.selectedClientId = null;
          this.setHeaderConfig();
          this.toastr.error(err.message || 'Impossible d\'enregistrer le client.');
          this.cdr.detectChanges();
        },
      });
  }

  confirmFinalize(): void {
    if (!this.canFinalize()) {
      return;
    }
    void Swal.fire({
      icon: 'question',
      title: 'Terminer la facture ?',
      html: `La facture <strong>${this.numDocument}</strong> sera finalisée et ne sera plus modifiable.`,
      showCancelButton: true,
      focusCancel: true,
      confirmButtonText: 'Terminé',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#134ad3',
      reverseButtons: true,
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }
      this.finalizeDocument();
    });
  }

  private setHeaderConfig(): void {
    type BtnType = 'primary' | 'secondary';
    const buttons: Array<{ label: string; action: () => void; type: BtnType }> = [
      { label: 'Retour', action: () => this.goBack(), type: 'secondary' },
    ];
    if (this.canFinalize()) {
      buttons.push({
        label: 'Terminé',
        action: () => this.confirmFinalize(),
        type: 'primary',
      });
    }
    this.headerService.setConfig({
      title: this.isEditable ? 'Édition facture' : 'Détail facture',
      buttons,
    });
  }

  private loadDetail(forceEdit?: boolean): void {
    this.loading = true;
    this.ticketService.getTicketDetail(this.factureId).subscribe({
      next: (data) => {
        this.hydrate(data, forceEdit);
        this.loading = false;
        this.setHeaderConfig();
        this.cdr.detectChanges();
        setTimeout(() => feather.replace(), 80);
      },
      error: (err: Error) => {
        this.loading = false;
        this.toastr.error(err.message || 'Erreur chargement facture.');
        void this.router.navigateByUrl(this.returnUrl);
      },
    });
  }

  private hydrate(data: Record<string, unknown>, forceEdit?: boolean): void {
    const statut = String(data['statut'] ?? '').trim();
    this.documentStatut = this.formatStatutLabel(statut);
    this.isEditable = forceEdit === true || statut === 'en_cours';

    this.numDocument = String(data['code_document'] ?? '—');
    const rawDate = data['date'];
    if (rawDate) {
      const d = new Date(String(rawDate));
      this.documentDate = Number.isNaN(d.getTime())
        ? String(rawDate)
        : d.toLocaleDateString('fr-FR');
    }
    this.entrepotLabel = String(data['entrepot_libelle'] ?? '—');
    const docOrig = data['document_original'];
    this.documentOriginalLabel =
      docOrig != null && String(docOrig).trim() !== '' ? String(docOrig).trim() : '—';

    const rawClientId = data['client_id'];
    this.clientId =
      rawClientId != null && Number(rawClientId) > 0 ? Number(rawClientId) : null;
    this.selectedClientId = this.clientId;

    const client = data['client_detail'] as Record<string, unknown> | null;
    if (client) {
      const prenom = String(client['prenom'] ?? '').trim();
      const nom = String(client['nom'] ?? '').trim();
      this.clientLabel = [prenom, nom].filter(Boolean).join(' ') || '—';
      this.clientPhone =
        String(client['telephone'] ?? client['phone'] ?? '').trim() || '—';
    } else {
      this.clientLabel = String(data['client_phone'] ?? data['client_email'] ?? '—');
      this.clientPhone = String(data['client_phone'] ?? '—');
    }

    const lignesRaw = Array.isArray(data['lignes']) ? data['lignes'] : [];
    this.ligneRows = lignesRaw.map((item) => {
      const l = item as Record<string, unknown>;
      const couleur = String(l['couleur'] ?? '').trim();
      const taille = String(l['taille'] ?? '').trim();
      const qnt = Number(l['qnt'] ?? 0) || 0;
      const prix = Number(l['prix'] ?? 0) || 0;
      const total = Number(l['total'] ?? 0) || prix * qnt;
      return {
        ligneId: l['id'] != null ? Number(l['id']) : undefined,
        reference: String(l['code_barre'] ?? l['libelle'] ?? '—'),
        libelle: String(l['libelle'] ?? '—'),
        couleurTaille: [couleur, taille].filter(Boolean).join(' / ') || '—',
        quantite: qnt,
        prixUnitaire: prix,
        totalHt: total,
        productId: l['product_id'] != null ? Number(l['product_id']) : null,
        codeBarre: l['code_barre'] != null ? String(l['code_barre']) : null,
        couleur: couleur || null,
        taille: taille || null,
      };
    });

    const sources = Array.isArray(data['source_tickets']) ? data['source_tickets'] : [];
    this.sourceTickets = sources.map((item) => {
      const s = item as Record<string, unknown>;
      return {
        id: Number(s['id'] ?? 0),
        numero: String(s['code_document'] ?? '—'),
        date: s['date'] ? new Date(String(s['date'])).toLocaleDateString('fr-FR') : '—',
        entrepot: String(s['entrepot_libelle'] ?? '—'),
        statut: this.formatStatutLabel(String(s['statut'] ?? '')),
        client: String(s['client_label'] ?? '—'),
        total: Number(s['total'] ?? 0) || 0,
      };
    });

    this.recalculateTotals();

    if (this.showClientPicker) {
      this.loadClientOptions();
    }
  }

  private clientOptionLabel(c: CaisseClient): string {
    return [c.prenom, c.nom].filter(Boolean).join(' ').trim() || '—';
  }

  private loadClientOptions(): void {
    this.loadingClients = true;
    this.clientService.getClients({ page: 1, limit: 100 }).subscribe({
      next: (res) => {
        this.clientOptions = res.items.map((c) => ({
          id: c.id,
          label: this.clientOptionLabel(c),
          email: c.email ?? null,
          phone: c.telephone ?? null,
        }));
        this.loadingClients = false;
        this.cdr.detectChanges();
      },
      error: (err: Error) => {
        this.loadingClients = false;
        this.toastr.error(err.message || 'Impossible de charger les clients.');
        this.cdr.detectChanges();
      },
    });
  }

  private recalculateTotals(): void {
    const totalHT = this.ligneRows.reduce((s, r) => s + (Number(r.totalHt) || 0), 0);
    const totalTVA = 0;
    const totalTTC = totalHT + totalTVA;
    this.totals = {
      totalHT,
      totalTVA,
      totalTTC,
      timbreFiscal: 1,
      netAPayer: totalTTC + 1,
    };
    this.setHeaderConfig();
    this.cdr.detectChanges();
  }

  private finalizeDocument(): void {
    this.finalizing = true;
    this.setHeaderConfig();
    const lignes = this.ligneRows.map((row) => ({
      libelle: row.libelle,
      code_barre: row.codeBarre,
      couleur: row.couleur,
      taille: row.taille,
      prix: row.prixUnitaire,
      qnt: row.quantite,
      remise: 0,
      total: row.totalHt,
      product_id: row.productId,
    }));

    this.ticketService
      .finalizeFacture(this.factureId, { lignes, client_id: this.clientId ?? undefined })
      .subscribe({
      next: () => {
        this.finalizing = false;
        this.isEditable = false;
        this.documentStatut = 'Terminé';
        this.toastr.success('Facture finalisée.', 'Facturation');
        this.setHeaderConfig();
        void this.router.navigateByUrl(this.returnUrl);
      },
      error: (err: Error) => {
        this.finalizing = false;
        this.setHeaderConfig();
        this.toastr.error(err.message || 'Finalisation impossible.', 'Erreur');
        this.cdr.detectChanges();
      },
    });
  }
}
